import { createContext, useCallback, useContext, useState } from "react";
import type { ReactNode } from "react";
import type { UploadResponse } from "./types";

export type PhotoStatus = "pending" | "uploading" | "done" | "error";

export type UploadPhase = "uploading" | "analyzing";

export type LocalPhoto = {
  id: string;
  file: File;
  previewUrl: string;
  status: PhotoStatus;
  phase?: UploadPhase;
  progress?: number;
  serverResult?: UploadResponse;
  error?: string;
};

export type Screen = "upload" | "processing" | "summary";

type InspectionContextValue = {
  screen: Screen;
  photos: LocalPhoto[];
  sessionId: string | null;
  addFiles: (files: FileList | File[]) => {
    accepted: number;
    rejected: number;
  };
  removePhoto: (id: string) => void;
  submit: () => Promise<void>;
  reset: () => void;
};

const InspectionContext = createContext<InspectionContextValue | null>(null);

const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png"]);

function makeLocalId() {
  return crypto.randomUUID();
}

export function InspectionProvider({ children }: { children: ReactNode }) {
  const [screen, setScreen] = useState<Screen>("upload");
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const addFiles = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    const accepted: File[] = [];
    let rejected = 0;
    for (const f of arr) {
      if (ACCEPTED_TYPES.has(f.type)) accepted.push(f);
      else rejected++;
    }

    if (accepted.length > 0) {
      setPhotos((prev) => {
        const next = [...prev];
        for (const f of accepted) {
          next.push({
            id: makeLocalId(),
            file: f,
            previewUrl: URL.createObjectURL(f),
            status: "pending",
          });
        }
        return next;
      });
    }

    return { accepted: accepted.length, rejected };
  }, []);

  const removePhoto = useCallback((id: string) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const submit = useCallback(async () => {
    let currentPhotos: LocalPhoto[] = [];
    setPhotos((prev) => {
      currentPhotos = prev;
      return prev;
    });
    if (currentPhotos.length === 0) return;

    setScreen("processing");

    const sessRes = await fetch("/api/sessions", { method: "POST" });
    const { sessionId: sid } = (await sessRes.json()) as {
      sessionId: string;
    };
    setSessionId(sid);

    const patch = (id: string, fields: Partial<LocalPhoto>) => {
      setPhotos((prev) =>
        prev.map((x) => (x.id === id ? { ...x, ...fields } : x)),
      );
    };

    await Promise.all(
      currentPhotos.map(async (p) => {
        patch(p.id, {
          status: "uploading",
          phase: "uploading",
          progress: 0,
        });
        try {
          const result = await uploadAndAnalyse(sid, p, patch);
          patch(p.id, {
            status: "done",
            phase: undefined,
            progress: 100,
            serverResult: result,
          });
        } catch (e) {
          patch(p.id, {
            status: "error",
            phase: undefined,
            error: (e as Error).message,
          });
        }
      }),
    );

    setScreen("summary");
  }, []);

  const reset = useCallback(() => {
    setPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
    setSessionId(null);
    setScreen("upload");
  }, []);

  return (
    <InspectionContext.Provider
      value={{
        screen,
        photos,
        sessionId,
        addFiles,
        removePhoto,
        submit,
        reset,
      }}
    >
      {children}
    </InspectionContext.Provider>
  );
}

function uploadAndAnalyse(
  sessionId: string,
  photo: LocalPhoto,
  patch: (id: string, fields: Partial<LocalPhoto>) => void,
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const fd = new FormData();
    fd.append("file", photo.file);

    let analyseTicker: ReturnType<typeof setInterval> | null = null;
    const clearTicker = () => {
      if (analyseTicker) clearInterval(analyseTicker);
      analyseTicker = null;
    };

    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      const pct = Math.min(100, Math.round((e.loaded / e.total) * 100));
      patch(photo.id, { phase: "uploading", progress: pct });
    };

    xhr.upload.onload = () => {
      patch(photo.id, { phase: "analyzing", progress: 0 });
      const startedAt = Date.now();
      analyseTicker = setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const pct = Math.min(
          95,
          Math.round(95 * (1 - Math.exp(-elapsed / 2000))),
        );
        patch(photo.id, { phase: "analyzing", progress: pct });
      }, 120);
    };

    xhr.onload = () => {
      clearTicker();
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadResponse);
        } catch {
          reject(new Error("invalid JSON response"));
        }
      } else {
        reject(new Error(`upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      clearTicker();
      reject(new Error("network error"));
    };

    xhr.open("POST", `/api/sessions/${sessionId}/photos`);
    xhr.send(fd);
  });
}

export function useInspection() {
  const ctx = useContext(InspectionContext);
  if (!ctx)
    throw new Error("useInspection must be used within InspectionProvider");
  return ctx;
}
